import { Component, OnInit } from '@angular/core';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {ModalService} from "../../../../services/ven/modal.service";
import {NgClass, TitleCasePipe} from "@angular/common";
import {SearchFilterModule} from "../../../common/searchFilter/searchFilter.module";
import {NgxPaginationModule} from "ngx-pagination";
import {
  GroupSubModel, MarketContextSubModel, MetadataReportSubModel, PlatformSubModel,
  ResourceSubModel,
  VenDto,
  VenEnvironment,
  VenProfile,
  VenMode, VenViewModel
} from "../../../../model/vens.model";
import {VtnViewModel} from "../../../../model/vtns.model";
import {environment} from "../../../../../environments/environment";
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-create-ven',
  templateUrl: './create-ven.component.html',
  styleUrls: ['./create-ven.component.scss'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NgClass,
    SearchFilterModule,
    NgxPaginationModule,
    FormsModule,
    TitleCasePipe
  ],
})
export class CreateVenComponent implements OnInit {
  protected readonly Array = Array;
  venForm = new FormGroup({
    id: new FormControl<number | null>(null),
    ven_id: new FormControl<string>('', [Validators.required]),
    name: new FormControl<string>('', [Validators.required]),
    profile: new FormControl<VenProfile>('2.0b', [Validators.required]),
    mode: new FormControl<VenMode>('poll', [Validators.required]),
    vtnId: new FormControl<any>(null),
    environment: new FormControl<VenEnvironment>('X-Energy', [Validators.required]),
    allowUpdateOadrEvent: new FormControl<boolean>(false),
    allowEndDateUpdate: new FormControl<boolean>(true),
    emsCheckEnabled: new FormControl<boolean>(false),
    cancelEnabled: new FormControl<boolean>(false),
    reportEnabled: new FormControl<boolean>(false),
    registrationCancelEnabled: new FormControl<boolean>(true),
    sendReportCancellationEnabled: new FormControl<boolean>(true),
    includeEventObligations: new FormControl<boolean>(true),
    allowUpdateNonOadrEvent: new FormControl<boolean>(false),
    allowedToMatchPartial: new FormControl<boolean>(false),
    secondaryVtn: new FormControl<any>(null),
    platform: new FormControl<PlatformSubModel | null>(null),
  });
  error: string | null = null;
  editing = false;
  ven_id: string | null = null;
  secondaryVtnToggle = false;

  // Mode options for dropdown
  public modeOptions: { value: VenMode; label: string }[] = [
    { value: 'poll', label: 'Poll' },
    { value: 'push', label: 'Push' }
  ];

  // Data arrays
  vtnsSelectable: VtnViewModel[] = [];
  vtnsSelectableFiltered: VtnViewModel[] = [];
  platformsSelectable: PlatformSubModel[] = [];

  // Selected data
  vtnSelected: VtnViewModel;
  groupsSelected: GroupSubModel[] = [];
  resourcesSelected: ResourceSubModel[] = [];
  marketContextsSelected: MarketContextSubModel[] = [];
  metadataReportsSelected: MetadataReportSubModel[] = [];

  // Possible data
  displayedPossibleGroups: GroupSubModel[] = [];
  displayedPossibleResources: ResourceSubModel[] = [];
  displayedPossibleMarketContext: MarketContextSubModel[] = [];
  displayedPossibleMetadataReport: MetadataReportSubModel[] = [];

  // Search filters
  searchTextGroup = '';
  searchTextPossibleGroup = '';
  searchTextResources = '';
  searchTextPossibleResources = '';
  searchTexMarketContext = '';
  searchTextPossibleMarketContext = '';
  searchTexMetadataReport = '';
  searchTextPossibleMetadataReport = '';

  // Pagination configuration
  groupSelectedPagination = { id: 'groupSelectedPagination', itemsPerPage: 6, currentPage: 1 };
  displayedPossibleGroupsPagination = { id: 'displayedPossibleGroupsPagination', itemsPerPage: 6, currentPage: 1 };
  resourcesSelectedPagination = { id: 'resourcesSelectedPagination', itemsPerPage: 6, currentPage: 1 };
  displayedPossibleResourcesPagination = { id: 'displayedPossibleResourcesPagination', itemsPerPage: 6, currentPage: 1 };
  marketContextSelectedPagination = { id: 'marketContextSelectedPagination', itemsPerPage: 6, currentPage: 1 };
  displayedPossibleMarketContextPagination = { id: 'displayedPossibleMarketContextPagination', itemsPerPage: 6, currentPage: 1 };
  metadataReportSelectedPagination = { id: 'metadataReportSelectedPagination', itemsPerPage: 6, currentPage: 1 };
  displayPossibleMetadataReportPagination = { id: 'displayPossibleMetadataReportPagination', itemsPerPage: 6, currentPage: 1 };

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private modalService: ModalService
  ) {}

  ngOnInit(): void {
    // Check if we're editing an existing VEN
    const venId = this.route.snapshot.paramMap.get('id');

    if (venId) {
      this.editing = true;
      this.ven_id = venId;
      this.loadVen(venId);
    } else {
      this.loadVenAssetsUnfiltered();
    }

    // Subscribe to mode changes to filter VTNs
    // Only clear VTN selection when not editing (to avoid clearing during initial data load)
    this.venForm.get('mode')?.valueChanges.subscribe(mode => {
      this.filterVtnsByMode(mode);
      // Only clear selected VTN when creating a new VEN (not during edit load)
      if (!this.editing) {
        this.venForm.get('vtnId')?.setValue(null);
        this.venForm.get('secondaryVtn')?.setValue(null);
      }
    });

    // Hierarchical toggle logic for non-OADR event updates
    this.venForm.get('allowUpdateNonOadrEvent')?.valueChanges.subscribe(enabled => {
      if (!enabled) {
        // When parent is disabled, disable and reset child option
        this.venForm.get('allowedToMatchPartial')?.setValue(false, { emitEvent: false });
        this.venForm.get('allowedToMatchPartial')?.disable({ emitEvent: false });
      } else {
        // When parent is enabled, enable child option
        this.venForm.get('allowedToMatchPartial')?.enable({ emitEvent: false });
      }
    });

    // Hierarchical toggle logic for OADR event updates
    this.venForm.get('allowUpdateOadrEvent')?.valueChanges.subscribe(enabled => {
      if (!enabled) {
        // When parent is disabled, disable and reset child option to default
        this.venForm.get('allowEndDateUpdate')?.setValue(true, { emitEvent: false });
        this.venForm.get('allowEndDateUpdate')?.disable({ emitEvent: false });
        // When OADR updates disabled, ensure cancellation is also properly set
        this.venForm.get('cancelEnabled')?.enable({ emitEvent: false });
      } else {
        // When parent is enabled, enable child option
        this.venForm.get('allowEndDateUpdate')?.enable({ emitEvent: false });
      }
    });

    // Event cancellation dependency on allowEndDateUpdate
    this.venForm.get('allowEndDateUpdate')?.valueChanges.subscribe(allowEndDate => {
      if (!allowEndDate) {
        // When "only instructions" is selected, disable and uncheck event cancellation
        this.venForm.get('cancelEnabled')?.setValue(false, { emitEvent: false });
        this.venForm.get('cancelEnabled')?.disable({ emitEvent: false });
      } else {
        // When "duration and instructions" is selected, enable event cancellation
        this.venForm.get('cancelEnabled')?.enable({ emitEvent: false });
      }
    });

    // Initialize state based on current values (only for new VENs)
    if (!this.editing) {
      if (!this.venForm.get('allowUpdateNonOadrEvent')?.value) {
        this.venForm.get('allowedToMatchPartial')?.disable({ emitEvent: false });
      }
      if (!this.venForm.get('allowUpdateOadrEvent')?.value) {
        this.venForm.get('allowEndDateUpdate')?.disable({ emitEvent: false });
      }
      if (!this.venForm.get('allowEndDateUpdate')?.value) {
        this.venForm.get('cancelEnabled')?.disable({ emitEvent: false });
      }
    }
  }

  /**
   * Filter VTNs based on the selected VEN mode
   */
  filterVtnsByMode(venMode: VenMode | null): void {
    if (!venMode || !this.vtnsSelectable) {
      this.vtnsSelectableFiltered = [];
      return;
    }

    // Only show VTNs that match the VEN mode
    this.vtnsSelectableFiltered = this.vtnsSelectable.filter(vtn => {
      // Default to 'poll' mode for backward compatibility
      const vtnMode = vtn.mode || 'poll';
      return vtnMode === venMode;
    });
  }

  loadVen(id: string): void {
    this.modalService.get('ven', { id, depth: 2 }).subscribe(
      (vens: VenViewModel[]) => {
        if (vens && vens.length) {
          const ven = vens[0];

          // Load VTNs and Platforms first, then set everything
          forkJoin({
            vtns: this.modalService.get('vtn', { depth: 1 }),
            platforms: this.modalService.get('platform', { depth: 1 })
          }).subscribe({
            next: (data: any) => {
              // Set VTNs first
              this.vtnsSelectable = data.vtns;
              this.platformsSelectable = data.platforms;

              // Now filter by the VEN's mode
              this.filterVtnsByMode(ven.mode || 'poll');

              // Set toggle for secondary VTN
              if (ven.secondary_vtn) {
                this.secondaryVtnToggle = true;
              }

              // Set selected items
              this.vtnSelected = ven.vtn;
              this.groupsSelected = ven.groups;
              this.resourcesSelected = ven.resources;
              this.marketContextsSelected = ven.market_contexts;
              this.metadataReportsSelected = ven.metadata_reports;

              // Set form values
              this.venForm.patchValue({
                id: ven.id,
                ven_id: ven.ven_id,
                name: ven.name,
                profile: ven.profile,
                mode: ven.mode,
                allowUpdateOadrEvent: ven.allow_update_oadr_event,
                allowEndDateUpdate: ven.allow_end_date_update,
                emsCheckEnabled: ven.validate_against_ems,
                cancelEnabled: ven.cancel_enabled,
                reportEnabled: ven.report_enabled,
                registrationCancelEnabled: ven.registration_cancel_enabled,
                sendReportCancellationEnabled: ven.send_report_cancellation_enabled,
                includeEventObligations: ven.include_event_obligations,
                allowUpdateNonOadrEvent: ven.allow_update_non_oadr_event,
                allowedToMatchPartial: ven.allowed_to_match_partial,
                vtnId: ven.vtn,
                secondaryVtn: ven.secondary_vtn,
                environment: ven.environment,
                platform: ven.platform
              });

              // Disable id field when editing
              this.venForm.get('id')?.disable();
              if (this.editing) {
                this.venForm.get('ven_id')?.disable();
                this.venForm.get('mode')?.disable();
              }

              // Ensure VTN is set correctly after all subscriptions have fired
              // This is a safeguard in case valueChanges cleared it
              if (ven.vtn) {
                this.venForm.get('vtnId')?.setValue(ven.vtn, { emitEvent: false });
              }
              if (ven.secondary_vtn) {
                this.venForm.get('secondaryVtn')?.setValue(ven.secondary_vtn, { emitEvent: false });
              }

              // Initialize hierarchical state for editing
              // Enable/disable child options based on parent values
              if (!ven.allow_update_non_oadr_event) {
                this.venForm.get('allowedToMatchPartial')?.disable({ emitEvent: false });
              } else {
                this.venForm.get('allowedToMatchPartial')?.enable({ emitEvent: false });
              }

              if (!ven.allow_update_oadr_event) {
                this.venForm.get('allowEndDateUpdate')?.disable({ emitEvent: false });
              } else {
                this.venForm.get('allowEndDateUpdate')?.enable({ emitEvent: false });
              }

              if (!ven.allow_end_date_update) {
                this.venForm.get('cancelEnabled')?.disable({ emitEvent: false });
              } else {
                this.venForm.get('cancelEnabled')?.enable({ emitEvent: false });
              }

              // Load the rest of the assets
              this.loadRemainingAssets(ven);
            },
            error: (error: any) => {
              this.error = error;
              console.error(error);
            }
          });
        }
      },
      (error: any) => {
        this.error = error;
        console.error(error);
      }
    );
  }

  private loadRemainingAssets(ven: any): void {
    // Load and filter Groups
    this.modalService.get('group', { depth: 1 }).subscribe(
      (groups: GroupSubModel[]) => {
        const venGroupIds = ven.groups.map((g: GroupSubModel) => g.id);
        this.displayedPossibleGroups = groups.filter(g => !venGroupIds.includes(g.id));
      },
      (error: any) => {
        this.error = error;
        console.error(error);
      }
    );

    // Load and filter Resources
    this.modalService.get('resource', { depth: 1 }).subscribe(
      (resources: ResourceSubModel[]) => {
        const venResourceIds = ven.resources.map((r: ResourceSubModel) => r.id);
        this.displayedPossibleResources = resources.filter(r => !venResourceIds.includes(r.id));
      },
      (error: any) => {
        this.error = error;
        console.error(error);
      }
    );

    // Load and filter Market Contexts
    this.modalService.get('market_context', { depth: 1 }).subscribe(
      (marketContexts: MarketContextSubModel[]) => {
        const venMarketContextIds = ven.market_contexts.map((m: MarketContextSubModel) => m.id);
        this.displayedPossibleMarketContext = marketContexts.filter(m => !venMarketContextIds.includes(m.id));
      },
      (error: any) => {
        this.error = error;
        console.error(error);
      }
    );

    // Load and filter Metadata Reports
    this.modalService.get('metadata_report', { depth: 1 }).subscribe(
      (metadataReports: MetadataReportSubModel[]) => {
        const venMetadataReportIds = ven.metadata_reports.map((m: MetadataReportSubModel) => m.id);
        this.displayedPossibleMetadataReport = metadataReports.filter(m => !venMetadataReportIds.includes(m.id));
      },
      (error: any) => {
        this.error = error;
        console.error(error);
      }
    );
  }

  loadVenAssetsUnfiltered(): void {
    // Load VTNs
    this.modalService.get('vtn', { depth: 1 }).subscribe(
      (vtns: VtnViewModel[]) => {
        this.vtnsSelectable = vtns;
        // Filter by default mode (poll) for new VENs
        this.filterVtnsByMode('poll');
      },
      (error: any) => {
        this.error = error;
        console.error(error);
      }
    );

    // Load Platforms
    this.modalService.get('platform', { depth: 1 }).subscribe(
      (platforms: PlatformSubModel[]) => {
        this.platformsSelectable = platforms;
      },
      (error: any) => {
        this.error = error;
        console.error(error);
      }
    );

    // Load Groups
    this.modalService.get('group', { depth: 1 }).subscribe(
      (groups: GroupSubModel[]) => {
        this.displayedPossibleGroups = groups;
      },
      (error: any) => {
        this.error = error;
        console.error(error);
      }
    );

    // Load Resources
    this.modalService.get('resource', { depth: 1 }).subscribe(
      (resources: ResourceSubModel[]) => {
        this.displayedPossibleResources = resources;
      },
      (error: any) => {
        this.error = error;
        console.error(error);
      }
    );

    // Load Market Contexts
    this.modalService.get('market_context', { depth: 1 }).subscribe(
      (marketContexts: MarketContextSubModel[]) => {
        this.displayedPossibleMarketContext = marketContexts;
      },
      (error: any) => {
        this.error = error;
        console.error(error);
      }
    );

    // Load Metadata Reports
    this.modalService.get('metadata_report', { depth: 1 }).subscribe(
      (metadataReports: MetadataReportSubModel[]) => {
        this.displayedPossibleMetadataReport = metadataReports;
      },
      (error: any) => {
        this.error = error;
        console.error(error);
      }
    );
  }

  // Handle page changes for pagination
  handlePageChange(event: number, paginationObject: any): void {
    paginationObject.currentPage = event;
  }

  // Group operations
  includeGroup(group: GroupSubModel): void {
    this.groupsSelected.push(group);
    this.displayedPossibleGroups = this.displayedPossibleGroups.filter(g => g.id !== group.id);
  }

  removeGroup(group: GroupSubModel): void {
    this.displayedPossibleGroups.push(group);
    this.groupsSelected = this.groupsSelected.filter(g => g.id !== group.id);
  }

  // Resource operations
  includeResource(resource: ResourceSubModel): void {
    this.resourcesSelected.push(resource);
    this.displayedPossibleResources = this.displayedPossibleResources.filter(r => r.id !== resource.id);
  }

  removeResource(resource: ResourceSubModel): void {
    this.displayedPossibleResources.push(resource);
    this.resourcesSelected = this.resourcesSelected.filter(r => r.id !== resource.id);
  }

  // Market context operations
  includeMarket(marketContext: MarketContextSubModel): void {
    this.marketContextsSelected.push(marketContext);
    this.displayedPossibleMarketContext = this.displayedPossibleMarketContext.filter(m => m.id !== marketContext.id);
  }

  removeMarket(marketContext: MarketContextSubModel): void {
    this.displayedPossibleMarketContext.push(marketContext);
    this.marketContextsSelected = this.marketContextsSelected.filter(m => m.id !== marketContext.id);
  }

  // Metadata report operations
  includeMetadata(metadataReport: MetadataReportSubModel): void {
    this.metadataReportsSelected.push(metadataReport);
    this.displayedPossibleMetadataReport = this.displayedPossibleMetadataReport.filter(m => m.id !== metadataReport.id);
  }

  removeMetadata(metadataReport: MetadataReportSubModel): void {
    this.displayedPossibleMetadataReport.push(metadataReport);
    this.metadataReportsSelected = this.metadataReportsSelected.filter(m => m.id !== metadataReport.id);
  }

  // Navigation
  backPage(path: string, router: Router): void {
    router.navigate([path]);
  }

  // Check for duplicate market contexts
  hasDuplicates(array: string[]): boolean {
    return (new Set(array)).size !== array.length;
  }

  get isPollMode(): boolean {
    return this.venForm.get('mode')?.value === 'poll';
  }

  get isPushMode(): boolean {
    return this.venForm.get('mode')?.value === 'push';
  }

  get pushUrl(): string {
    if (this.isPushMode) {
      return `https://ven.svcs.enelx.com/OpenADR2/Simple/2.0b/EiEvent`;
    }
    return '';
  }

  save(): void {
    if (this.venForm.invalid) {
      this.error = 'Please fill out all required fields correctly and try again.';
      return;
    }

    // Validate VTN mode matches VEN mode
    const venMode = this.venForm.get('mode')?.value;
    const selectedVtn = this.venForm.get('vtnId')?.value;

    if (!selectedVtn) {
      this.error = 'Please select a VTN that matches the VEN mode.';
      return;
    }

    const vtnMode = selectedVtn.mode || 'poll'; // Default to 'poll' for backward compatibility
    if (vtnMode !== venMode) {
      this.error = `VTN mode (${vtnMode}) must match VEN mode (${venMode}). Please select a compatible VTN.`;
      return;
    }

    // Check for duplicate market contexts
    const marketContexts = this.marketContextsSelected.map(mc => mc.market_context);
    if (this.hasDuplicates(marketContexts)) {
      this.error = 'Please insert only one Market Context with the same URI.';
      return;
    }

    // Prepare data for saving
    const formValues = this.venForm.getRawValue();

    const venData: VenDto = {
      id: formValues.id,
      ven_id: formValues.ven_id!,
      name: formValues.name!,
      profile: formValues.profile!,
      mode: formValues.mode!,
      allow_update_oadr_event: !!formValues.allowUpdateOadrEvent,
      allow_end_date_update: !!formValues.allowEndDateUpdate,
      cancel_enabled: !!formValues.cancelEnabled,
      report_enabled: !!formValues.reportEnabled,
      validate_against_ems: !!formValues.emsCheckEnabled,
      registration_cancel_enabled: !!formValues.registrationCancelEnabled,
      send_report_cancellation_enabled: !!formValues.sendReportCancellationEnabled,
      include_event_obligations: !!formValues.includeEventObligations,
      allow_update_non_oadr_event: !!formValues.allowUpdateNonOadrEvent,
      allowed_to_match_partial: !!formValues.allowedToMatchPartial,
      environment: formValues.environment!,
      platform_id: formValues.platform?.environment ?? null,
      vtn_id: formValues.vtnId?.id,
      secondary_vtn_id: formValues.secondaryVtn?.id,
      groups: this.groupsSelected.map(g => g.id),
      resources: this.resourcesSelected.map(r => r.id),
      market_contexts: this.marketContextsSelected.map(m => m.id),
      metadata_reports: this.metadataReportsSelected.map(m => m.id),
    };

    // Save the VEN
    if (this.editing) {
      // Update existing VEN
      this.modalService.update('ven', venData).subscribe(
        () => {
          this.router.navigate(['/vens']);
        },
        (error: any) => {
          this.error = error;
          console.error(error);
        }
      );
    } else {
      // Create new VEN
      this.modalService.create('ven', venData).subscribe(
        () => {
          this.router.navigate(['/vens']);
        },
        (error: any) => {
          this.error = error;
          console.error(error);
        }
      );
    }
  }

  /**
   * Compare function for VTN objects
   * Used by the select elements to determine which option is selected
   */
  compareById(item1: any, item2: any): boolean {
    // Handle null cases
    if (!item1 && !item2) {
      return true;
    }
    if (!item1 || !item2) {
      return false;
    }

    // Compare by id
    return item1.id === item2.id;
  }

  /**
   * Compare function for Platform objects
   * Used by the platform select element
   */
  compareByEnvironment(item1: any, item2: any): boolean {
    // Handle null cases
    if (!item1 && !item2) {
      return true;
    }
    if (!item1 || !item2) {
      return false;
    }

    // Compare by environment property
    return item1.environment === item2.environment;
  }
}
